/**
 * Centralised time-format helpers that honour the user's
 * `time_format` setting ("12h" by default, or "24h").
 *
 * Inputs are expected as either "HH:mm" or "HH:mm:ss" strings.
 */

const HH_MM_RE = /^(\d{1,2}):(\d{2})/;

function parse(time) {
  if (!time) return null;
  const m = HH_MM_RE.exec(String(time));
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (Number.isNaN(h) || Number.isNaN(min)) return null;
  return { h, min };
}

export function formatTime(time, format = "12h") {
  const p = parse(time);
  if (!p) return "";
  if (format === "24h") {
    return `${String(p.h).padStart(2, "0")}:${String(p.min).padStart(2, "0")}`;
  }
  const period = p.h >= 12 ? "PM" : "AM";
  const hour = p.h % 12 || 12;
  return `${hour}:${String(p.min).padStart(2, "0")} ${period}`;
}

/** Convenience: pull the format string off a user object, defaulting to 12h. */
export function timeFormatFor(user) {
  return user?.time_format === "24h" ? "24h" : "12h";
}

// React hook usage — needs to be imported separately to avoid pulling React
// into the bare util. See `useTimeFormatter` below.
import { useAuth } from "./AuthContext";

/**
 * React hook that returns a `(time) => string` formatter honouring the
 * currently-authenticated user's `time_format` setting. If there's no user
 * (e.g. on the public auth screens) it falls back to 12h.
 */
export function useTimeFormatter() {
  const { user } = useAuth();
  const fmt = timeFormatFor(user);
  return (time) => formatTime(time, fmt);
}
